/**
 * The ideal customer match as a criterion — Story 20.9, DECISION-138.
 *
 * PRD §3.8: "Semantic ICP match — fit against a natural-language ideal-customer-
 * profile description, as a 0–100 sub-score." Story 19.4 made the number; until
 * this story no criterion could read it, so a funnel with a profile paid the model
 * for a number nobody saw. Brief §6.1 says where it may go: "There is no separate
 * 'AI score' to reconcile with a 'rules score'." So it enters the one engine
 * through a criterion, at points the operator sets — never beside the total.
 *
 * OFFERED ONLY WITH SOMETHING TO MATCH AGAINST. The rubric refuses a `derived`
 * criterion while `icp_profile` is empty, so "Add a criterion" offers the match
 * only while there is a description, and says how to get one when there is not
 * (`derived-copy.ts`'s hint).
 */
import { applyOperation, type Criterion, type Funnel } from '@leadgible/schema'

export type Derived = Extract<Criterion, { source: 'derived' }>

/** Whether the funnel describes anyone a lead could be matched against. */
export const hasIcpProfile = (funnel: Funnel): boolean => (funnel.rubric.icp_profile ?? '').trim() !== ''

/** The criteria that read the fit. */
export const fitReaders = (funnel: Funnel): Derived[] =>
  funnel.rubric.criteria.filter((criterion): criterion is Derived => criterion.source === 'derived')

/** What "Add a criterion" offers the match on: a description, and no criterion reading the fit yet. */
export const offersFit = (funnel: Funnel): boolean => hasIcpProfile(funnel) && fitReaders(funnel).length === 0

/**
 * Adds the ideal customer match, as a recipe for `edit()`.
 *
 * At the inspector's `DEFAULT_WEIGHT`, like both other doors, and scoring zero
 * over the fit's whole 0–100 until the operator sets its ranges (DECISION-143's
 * rule, which the fit shares: the shape is picked, every number is theirs). With
 * no description, or with one criterion already reading the fit, it changes
 * nothing — the select never offers either, and a recipe should not depend on it.
 *
 * The vocabulary's `set_criterion` since Story 27.2: `crit_icp_fit` (or the next
 * free id), the drawing's "Ideal customer match" (`DERIVED_LABEL`, held equal to
 * `derived-copy.ts`'s `DRAWN.label`), the default weight, and one range over 0–100
 * at zero.
 */
export function addFitCriterion(draft: Funnel): void {
  if (!offersFit(draft)) return
  applyOperation(draft, { op: 'set_criterion', source: 'derived', key: 'icp_fit' })
}
